import { useState, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { addToastNotification } from 'store/notification/reducer';
import { cancelFunctions } from 'utils/cancelFunctions';
import { isCanceledError } from 'modules/api/request';
import {
  getEmailNotificationConfig,
  enableEmailNotification,
  disableEmailNotification,
} from 'modules/api/emailNotification';
import { EmailNotificationConfigDTO, LookupWindow } from 'components/common/types/EmailNotification.types';

const PERIOD_OPTIONS = ['Last 24 hours', 'Last 7 days', 'Last 30 days', 'Last 90 days', 'All time'] as const;
export type Period = (typeof PERIOD_OPTIONS)[number];

export const PERIOD_DATA_LIST = PERIOD_OPTIONS.map((option) => ({ label: option, value: option }));

const PERIOD_TO_LOOKUP_WINDOW: Record<Period, LookupWindow> = {
  'Last 24 hours': LookupWindow.LAST_24_HOURS,
  'Last 7 days': LookupWindow.LAST_7_DAYS,
  'Last 30 days': LookupWindow.LAST_30_DAYS,
  'Last 90 days': LookupWindow.LAST_90_DAYS,
  'All time': LookupWindow.ALL_TIME,
};

const LOOKUP_WINDOW_TO_PERIOD: Record<LookupWindow, Period> = {
  [LookupWindow.LAST_24_HOURS]: 'Last 24 hours',
  [LookupWindow.LAST_7_DAYS]: 'Last 7 days',
  [LookupWindow.LAST_30_DAYS]: 'Last 30 days',
  [LookupWindow.LAST_90_DAYS]: 'Last 90 days',
  [LookupWindow.ALL_TIME]: 'All time',
};

const applyConfig = (
  config: EmailNotificationConfigDTO,
  setIsEnabled: (v: boolean) => void,
  setSavedEmail: (v: string) => void,
  setSavedPeriod: (v: Period) => void,
): void => {
  setIsEnabled(config.enabled);
  setSavedEmail(config.recipientEmail);
  setSavedPeriod(LOOKUP_WINDOW_TO_PERIOD[config.lookupWindow] ?? 'Last 24 hours');
};

export interface NotificationState {
  isLoading: boolean;
  isSaving: boolean;
  isEnabled: boolean;
  savedEmail: string;
  savedPeriod: Period;
}

export interface UseEmailNotificationApiReturn extends NotificationState {
  handleSave: (draftEmail: string, draftPeriod: Period, wasPreviouslyEnabled: boolean) => Promise<void>;
  handleUnsubscribe: () => Promise<void>;
}

const useEmailNotificationApi = (
  environmentId: string,
  defaultEmail: string,
  dispatch: ReturnType<typeof useDispatch>,
): UseEmailNotificationApiReturn => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [savedEmail, setSavedEmail] = useState(defaultEmail);
  const [savedPeriod, setSavedPeriod] = useState<Period>('Last 24 hours');

  useEffect(() => {
    if (!environmentId) {
      setIsLoading(false);
      return undefined;
    }

    let isMounted = true;
    setIsLoading(true);

    const fetchConfig = async (): Promise<void> => {
      try {
        const config = await getEmailNotificationConfig(environmentId, cancelFunctions);
        if (!isMounted) return;

        if (config) {
          applyConfig(config, setIsEnabled, setSavedEmail, setSavedPeriod);
        } else {
          setIsEnabled(false);
          setSavedEmail(defaultEmail);
          setSavedPeriod('Last 24 hours');
        }
      } catch (error: unknown) {
        if (!isCanceledError(error)) {
          dispatch(
            addToastNotification({
              type: 'Failure',
              description: 'Failed to load email notification settings.',
              timeout: 2000,
            }),
          );
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchConfig();

    return () => {
      isMounted = false;
      if (cancelFunctions.FETCH_EMAIL_NOTIFICATION_CONFIG) {
        cancelFunctions.FETCH_EMAIL_NOTIFICATION_CONFIG();
        delete cancelFunctions.FETCH_EMAIL_NOTIFICATION_CONFIG;
      }
    };
  }, [environmentId, defaultEmail]);

  const handleSave = useCallback(
    async (draftEmail: string, draftPeriod: Period, wasPreviouslyEnabled: boolean): Promise<void> => {
      setIsSaving(true);
      try {
        const config = await enableEmailNotification(
          {
            recipientEmail: draftEmail,
            publisher: environmentId,
            lookupWindow: PERIOD_TO_LOOKUP_WINDOW[draftPeriod],
          },
          cancelFunctions,
        );

        applyConfig(config, setIsEnabled, setSavedEmail, setSavedPeriod);
        dispatch(
          addToastNotification({
            type: 'Success',
            description: wasPreviouslyEnabled
              ? 'Email notification settings saved successfully.'
              : 'Email notifications subscribed successfully.',
            timeout: 2000,
          }),
        );
      } catch (error: unknown) {
        if (!isCanceledError(error)) {
          dispatch(
            addToastNotification({
              type: 'Failure',
              description: 'Failed to save email notification settings. Please try again.',
              timeout: 2000,
            }),
          );
          throw error;
        }
      } finally {
        setIsSaving(false);
      }
    },
    [environmentId, dispatch],
  );

  const handleUnsubscribe = useCallback(async (): Promise<void> => {
    setIsSaving(true);
    try {
      await disableEmailNotification(environmentId, cancelFunctions);
      setIsEnabled(false);
      setSavedEmail(defaultEmail);
      setSavedPeriod('Last 24 hours');
      dispatch(
        addToastNotification({
          type: 'Success',
          description: 'Email notifications unsubscribed successfully.',
          timeout: 2000,
        }),
      );
    } catch (error: unknown) {
      if (!isCanceledError(error)) {
        dispatch(
          addToastNotification({
            type: 'Failure',
            description: 'Failed to unsubscribe from email notifications. Please try again.',
            timeout: 2000,
          }),
        );
      }
    } finally {
      setIsSaving(false);
    }
  }, [environmentId, defaultEmail, dispatch]);

  return {
    isLoading,
    isSaving,
    isEnabled,
    savedEmail,
    savedPeriod,
    handleSave,
    handleUnsubscribe,
  };
};

export default useEmailNotificationApi;
